import models.DomainGame
import models.GinkoEvent
import kotlin.random.Random

/**
 * The play-by-play color commentator. Diffs consecutive game snapshots to spot dramatic beats —
 * someone banks, a 7 torches the pot, the stakes climb, somebody wins — and returns a short, snarky
 * line to scroll across the ticker. Returns null when nothing noteworthy happened.
 *
 * Lines are templated with player names / pot size; a small recently-used buffer keeps it from
 * repeating itself. This runs per-device off the shared state stream, so it needs no extra plumbing
 * and works in local pass-and-play, online, and against bots alike.
 *
 * The copy here is a starter set — punch it up freely; the engine doesn't care what the strings say.
 */
class Commentator(private val random: Random = Random.Default) {
    private val recent = ArrayDeque<String>()
    private val recentCap = 14

    /** Pot thresholds that, when first crossed, are worth a "stakes are climbing" remark. */
    private val milestones = listOf(100, 150, 200, 300)

    fun reactTo(previous: DomainGame?, current: DomainGame?): String? {
        if (current == null) return null

        // Someone won — the loudest beat.
        if (current.finished && previous?.finished != true) {
            val winner = current.players.maxByOrNull { it.points } ?: return null
            return pick(winLines(winner.name, winner.points))
        }

        val prevRound = previous?.round ?: return null
        val curRound = current.round ?: return null

        // A round advanced. Carried-over dice (non-zero) mean a bust; zeroed dice mean everyone
        // banked the round out cleanly (no drama worth narrating).
        if (curRound.roundNum == prevRound.roundNum + 1) {
            val busted = curRound.dieOne != 0 || curRound.dieTwo != 0
            if (busted) {
                val victims = prevRound.activeOrderedPlayerNames
                if (victims.isNotEmpty()) {
                    val roller = curRound.rolledBy.ifEmpty { victims.first() }
                    return pick(bustLines(roller, victims, prevRound.currentPoints))
                }
            }
            return null
        }

        // Still the same round.
        if (curRound.roundNum == prevRound.roundNum) {
            // A player dropped out of the active list without the round ending → they banked.
            val banked = prevRound.activeOrderedPlayerNames
                .filterNot { it in curRound.activeOrderedPlayerNames }
            if (banked.isNotEmpty()) {
                return when (prevRound.ginkoEvent) {
                    GinkoEvent.HEIST_WINDOW -> pick(heistBankLines(banked.first(), curRound.activeOrderedPlayerNames))
                    GinkoEvent.MYSTERY_SURGE -> pick(mysteryBankLines(banked.first(), prevRound.currentPoints))
                    else -> pick(bankLines(banked.first(), prevRound.currentPoints, prevRound.currentRoll))
                }
            }

            if (curRound.ginkoEvent != GinkoEvent.NONE && curRound.ginkoEvent != prevRound.ginkoEvent) {
                return pick(ginkoEventLines(curRound.ginkoEvent, current.currentPlayer, curRound.currentPoints))
            }

            // A fresh roll pushed the pot past a milestone it hadn't reached before.
            if (curRound.currentRoll > prevRound.currentRoll) {
                val crossed = milestones.any { prevRound.currentPoints < it && curRound.currentPoints >= it }
                if (crossed) {
                    val roller = curRound.rolledBy.ifEmpty {
                        curRound.activeOrderedPlayerNames.firstOrNull() ?: ""
                    }
                    return pick(potLines(roller, curRound.currentPoints))
                }
            }
        }

        return null
    }

    private fun bankLines(name: String, pot: Int, rolls: Int): List<String> {
        val early = rolls <= 2
        return buildList {
            add("$name banked $pot and ran. Smart… or scared?")
            add("$name takes the money and leaves. Watch a 7 hit now and make them a genius.")
            add("$name is out with $pot safely in the bank.")
            add("Banked at $pot. $name has clearly been burned before.")
            if (early) {
                add("$name bailed after $rolls rolls? Living in fear.")
                add("Banking this early, $name? Bold strategy for a coward.")
            } else {
                add("$name rode it to $pot and walked away clean. Respect.")
            }
        }
    }

    private fun heistBankLines(name: String, riders: List<String>): List<String> {
        val who = formatNames(riders)
        return listOf(
            "$name banks and robs the riders on the way out. Legal? Somehow, yes.",
            "$name takes the Heist Window. $who just paid the convenience fee.",
            "That was not banking. That was theft with paperwork from $name.",
        )
    }

    private fun mysteryBankLines(name: String, pot: Int): List<String> = listOf(
        "$name banks blind and reveals $pot. Nerves: intact.",
        "Mystery Pot says $pot. $name trusted the vibes and lived.",
        "$name cracked the Mystery Pot at $pot. Everybody else was guessing.",
    )

    private fun ginkoEventLines(event: GinkoEvent, nextPlayer: String, pot: Int): List<String> = when (event) {
        GinkoEvent.NONE -> emptyList()
        GinkoEvent.DANGER_ZONE -> listOf(
            "Ginko just made 6 lethal. $nextPlayer suddenly has a math problem.",
            "Danger Zone: 6 busts next roll. The dice now have extra teeth.",
            "6 is live, 7 is still rude, and $pot is sitting there looking delicious.",
        )
        GinkoEvent.HEIST_WINDOW -> listOf(
            "Heist Window is open. Banking is now technically theft.",
            "Next banker skims the riders. Friendship was nice while it lasted.",
            "Ginko says: take the pot, take their points, take the blame.",
        )
        GinkoEvent.MYSTERY_SURGE -> listOf(
            "Mystery Pot activated. Everyone is guessing with their whole chest.",
            "Ginko hid the pot. Bank on instinct or roll on denial.",
            "Nobody knows what the pot is worth now. Perfectly normal, very fair.",
        )
        GinkoEvent.LAST_CALL -> listOf(
            "Last Call. Bank now or banking closes until the turn comes back around.",
            "One more chance to bank before Ginko shuts the door for a lap.",
            "Last Call is live. Cowards and geniuses look identical for one turn.",
        )
        GinkoEvent.ALL_IN -> listOf(
            "Bank Lockout. Nobody banks until the table survives a lap.",
            "The door is locked until the turn comes back around. Everybody rides now.",
            "Banking is closed for the lap. Ginko is enjoying this.",
        )
        GinkoEvent.HOT_STREAK -> listOf(
            "Hot Streak: next safe roll pays double. Greed has entered the chat.",
            "The next survivor gets paid twice. $nextPlayer, no pressure.",
            "Ginko lit the fuse. Safe roll doubles the gain.",
        )
    }

    private fun bustLines(roller: String, victims: List<String>, pot: Int): List<String> {
        val who = formatNames(victims)
        return listOf(
            "$roller rolls a 7 and torches $pot. $who left with nothing.",
            "And there it is — $pot up in smoke, courtesy of $roller.",
            "$who rode it all the way to $pot and got NOTHING. Brutal.",
            "$roller just deleted $pot from existence. Ouch.",
            "The 7 always comes. $who learns that the hard way — $pot gone.",
        )
    }

    private fun potLines(roller: String, pot: Int): List<String> = listOf(
        "Pot's at $pot and climbing. Somebody's about to get greedy.",
        "$pot in the middle and nobody's banking. Bold table.",
        "$pot on the line. The 7 is coming… it always comes.",
        "$roller pushes it to $pot. No fear in this one.",
    )

    private fun winLines(name: String, points: Int): List<String> = listOf(
        "$name wins it all with $points. Bow down.",
        "$name takes the crown. The rest of you played yourselves.",
        "Game over — $name walks away with $points and the bragging rights.",
    )

    /** "A", "A and B", or "A and N others". */
    private fun formatNames(names: List<String>): String = when (names.size) {
        0 -> "Nobody"
        1 -> names[0]
        2 -> "${names[0]} and ${names[1]}"
        else -> "${names[0]} and ${names.size - 1} others"
    }

    /** Pick a line, preferring ones not used recently so the ticker doesn't repeat itself. */
    private fun pick(options: List<String>): String {
        val pool = options.filterNot { it in recent }.ifEmpty { options }
        val chosen = pool[random.nextInt(pool.size)]
        recent.addLast(chosen)
        while (recent.size > recentCap) recent.removeFirst()
        return chosen
    }
}
